import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { fileToBase64, generateId, cn } from '@/lib/app-utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { createNotification } from '@/lib/audit';
import { alertsAPI } from '@/lib/alerts'; // ✅ إضافة API import

const AlertUploadDialog = ({ isOpen, onClose, onUploadSuccess }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium'
  });

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validation
    if (!selectedFile.type.startsWith('image/')) {
      toast({ 
        title: "Invalid file type", 
        description: "Please upload an image file.", 
        variant: "destructive" 
      });
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) { // 5MB
      toast({ 
        title: "File too large", 
        description: "Image must be under 5MB.", 
        variant: "destructive" 
      });
      return;
    }

    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ✅ الدالة الجديدة - إرسال للـ API
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !formData.title.trim()) {
      toast({ 
        title: "Missing fields", 
        description: "Please add title and image.", 
        variant: "destructive" 
      });
      return;
    }

    setIsLoading(true);

    try {
      const base64File = await fileToBase64(file);

      // ✅ إرسال للـ API مع حفظ الصورة كفايل
      const response = await alertsAPI.create({
        title: formData.title.trim(),
        message: formData.description.trim() || 'No description provided',
        type: 'info',
        priority: formData.priority.toLowerCase(), // 'high', 'medium', 'low'
        fileId: base64File, // سيتم حفظها كفايل في create.php
        fileName: file.name,
        createdBy: user.id,
        createdByName: user.name
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create alert');
      }

      // إنشاء notification للكل
      await createNotification('ALL', 'ALERT', `New Important Alert: ${formData.title}`);

      toast({
        title: "✅ Alert Published",
        description: `The alert "${formData.title}" has been saved to database and image uploaded.`,
        className: "bg-green-50 text-green-800 border-green-200"
      });

      onUploadSuccess();
      handleClose();

    } catch (error) {
      console.error('Upload error:', error);
      toast({ 
        title: "❌ Upload Failed", 
        description: error.message || "Could not save the alert.", 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setFormData({ title: '', description: '', priority: 'Medium' });
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📢 Upload System Alert Image</DialogTitle>
          <DialogDescription>
            Upload an important announcement image. All employees will be notified.
          </DialogDescription>
        </DialogHeader>

        {/* Image Upload Area */}
        <div className="space-y-4">
          <Label htmlFor="file-upload" className="flex items-center gap-2 cursor-pointer">
            <ImageIcon className="w-5 h-5" />
            Alert Image *
          </Label>
          
          {!preview ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            >
              <Upload className="w-12 h-12 mx-auto text-slate-400 mb-2" />
              <p className="text-sm font-medium text-slate-600">Click to upload image</p>
              <p className="text-xs text-slate-500">JPG, PNG, WebP (Max 5MB)</p>
              <input
                ref={fileInputRef}
                id="file-upload"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleFileSelect}
              />
            </div>
          ) : (
            <div className="relative group">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-64 object-cover rounded-lg shadow-md"
              />
              <button
                onClick={handleRemoveFile}
                className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50"
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4 pt-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              placeholder="e.g. Emergency Safety Notice"
            />
          </div>

          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="resize-none h-20"
              placeholder="Additional details about the alert..."
            />
          </div>

          <div>
            <Label htmlFor="priority">Priority Level</Label>
            <Select value={formData.priority} onValueChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">Low (Green)</SelectItem>
                <SelectItem value="Medium">Medium (Yellow)</SelectItem>
                <SelectItem value="High">High (Red)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!file || !formData.title.trim() || isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              '🚀 Post Alert'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AlertUploadDialog;
